import React, { useEffect, useRef, useState } from 'react';
import './WidgetPopup.scss';
import PropTypes from 'prop-types';
// import { sendLogData } from '../../../../common/actions/loggerAction';
// import { loggingEventName } from '../../../../common/constants/clickStreamEvents';
import { getOmniWidgetBaseURL } from 'common/utils/app.utils';

let lastHeartBeatTimestamp = 0;
let widgetLoadStartTime;
let defaultUrl = getOmniWidgetBaseURL();
const HEARTBEAT_INTERVAL_TIMEOUT = 6000;
let HB_PIXEL_FIRED = false;
let widgetLoadTime = new Date().getTime();

/**
 * This is a popup component to render the widget
 * @class
 * @param {WidgetPopup~propTypes} props
 * @returns {ReactElement}
 */
const WidgetPopup = (props) => {
  //State properties
  const [widgetLoaded, setWidgetLoaded] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [disableHeartbeat, setDisableHeartbeat] = useState(true);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const widgetFrameRef = useRef(null);
  const [widgetUrl, setWidgetUrl] = useState(defaultUrl + `&channelCity=${encodeURIComponent(props.selectedCity)}`);

  /**
   * Handles events coming from widget and peforms appropriate actions in response to them.
   * @method
   */
  useEffect(() => {
    widgetLoadStartTime = Date.now();
    // sendLogData({
    // 	data: {
    // 		eventName: loggingEventName.OMNIWIDGET_MOUNTED
    // 	},
    // });
    const eventHandler = (event) => {
      if (event?.data?.eventName && event?.data?.eventName?.startsWith('WtoC')) {
        switch (event?.data?.eventName) {
          case 'WtoC_PageLoaded':
            // sendLogData({
            // 	data: {
            // 		eventName: loggingEventName.OMNIWIDGET_LOADED_SUCCESS,
            // 		pageloadTime: Date.now() - widgetLoadStartTime,
            // 	},
            // });
            setWidgetLoaded(true);
            setDisableHeartbeat(false);
            resetRetryCount();
            break;
          case 'WtoC_HealthCheck': {
            !widgetLoaded && event?.data?.isLoaded && setWidgetLoaded(true);
            if (!HB_PIXEL_FIRED) {
              HB_PIXEL_FIRED = true;
              // sendLogData({
              //     data: {
              //         eventName: loggingEventName.OMNIWIDGET_HB_RECEIVED,
              //         pageloadTime: Date.now() - widgetLoadStartTime,
              //     },
              // });
            }
            if (event.data?.isError) {
              reloadWidget();
              return;
            }
            lastHeartBeatTimestamp = new Date().getTime();
            break;
          }
          case 'WtoC_ErrorLogged':
            // sendLogData({
            // 	data: {
            // 		eventName: loggingEventName.OMNIWIDGET_ERROR_RECEIVED,
            // 		error: event?.data?.errorLogs?.message,
            // 	},
            // });
            break;
          case 'WtoC_Submitted':
            props.onAddressSubmit(event.data?.userData);
            break;

          case 'WtoC_AddressDetails':
            props.onSubmit(event.data?.userData);
            break;

          // case 'WtoC_UserInteraction': {
          //   if (event.data?.userData?.id === 'pincodeInput' && event.data?.userData?.type === 'onBlur') {
          //     props.onPinCodeChange(event.data?.userData?.value);
          //   }
          //   break;
          // }
        }
      }
    };
    window.addEventListener('message', eventHandler);
    return () => {
      window.removeEventListener('message', eventHandler);
    };
  }, []);

  /**
   * Handles retry logic for widget
   * Widget sends heart beat event after every 500ms, If we don't receive heart beat
   * for more than 2 seconds we need to retry loading widget. Maximum 3 retries are allowed and
   * in each retry timeout would increase.
   * @method
   */
  useEffect(() => {
    if (widgetLoaded) {
      retryCount && resetRetryCount();
      return;
    }

    const timerId = setTimeout(() => {
      if (retryCount >= 3) {
        //sendLogData({ data: { eventName: loggingEventName.OMNIWIDGET_LOAD_FAILED, index: retryCount } });
        setShowErrorPopup(true);
        return;
      }
      if (!widgetLoaded) {
        widgetLoadTime = new Date().getTime();
        reloadWidget();
      }
    }, 5000 + retryCount * 2000);

    return () => {
      clearTimeout(timerId);
    };
  }, [retryCount, widgetLoaded]);

  /**
   * Handles heart beat logic for widget. If we do not receive heart beat for 2 seconds
   * We will reload widget inside iframe.
   * @method
   */
  useEffect(() => {
    if (disableHeartbeat) return;

    const intervalId = setInterval(() => {
      const currentTime = new Date().getTime();
      if (currentTime - lastHeartBeatTimestamp > HEARTBEAT_INTERVAL_TIMEOUT) {
        widgetLoadTime = new Date().getTime();
        reloadWidget();
      }
    }, HEARTBEAT_INTERVAL_TIMEOUT);

    return () => {
      clearTimeout(intervalId);
    };
  }, [disableHeartbeat]);

  const getWidgetURL = () => {
    let url = defaultUrl;
    if (props.isLocationAllowed) url += '&userLocationAllowed=true';
    if (props.selectedCity) {
      const city = encodeURIComponent(props.selectedCity);
      url += `&channelCity=${city}`;
    }
    if (props.editButtonClick) {
      url += `&editButtonClick=${props.editButtonClick}`;
    }
    if (widgetLoadTime) {
      url += `&widgetLoadTime=${widgetLoadTime}`;
    }
    return url;
  };

  /**
   * Responsible for reloading widget and resetting associated state properties.
   * @method
   */
  const reloadWidget = () => {
    // sendLogData({
    // 	data: {
    // 		eventName: loggingEventName.OMNIWIDGET_RELOAD_TRIGERRED,
    //         index: retryCount
    // 	},
    // });
    setWidgetLoaded(false);
    setRetryCount((prevRetryCount) => prevRetryCount + 1);
    if (!disableHeartbeat) setDisableHeartbeat(true);
    widgetLoadStartTime = Date.now();

    setWidgetUrl(getWidgetURL());
    // widgetFrameRef.current.contentWindow.location = url;
  };

  /**
   * Handler for closing popup when overlay is clicked.
   * @method
   */
  const handleOverlayClick = () => {
    props.onPopupClose();
  };

  /**
   * Responsible for resetting retry count.
   * @method
   */
  const resetRetryCount = () => {
    setRetryCount(0);
  };

  useEffect(() => {
    if (getWidgetURL() !== widgetUrl) {
      reloadWidget();
      resetRetryCount();
    }
  }, [props.isLocationAllowed, props.editButtonClick]);

  const refreshPage = () => {
    window.location.reload();
  };

  return (
    <div style={{ visibility: props.isOpen ? 'visible' : 'hidden' }} className="overlay" onClick={handleOverlayClick}>
      <div className="overlay-content">
        <div className="header-container">
          {/* <SwipeEventHandler componentId={'omniwidgetModalPopUp'} onSwipeDown={props.onPopupClose}>
						<div className='smallborder-wrap' onClick={props.onPopupClose}>
							<div className='smallborder'></div>
						</div>
					</SwipeEventHandler> */}
          <div className="head_content">
            <div className="heading-container">Check for service availability</div>
            <p>Installation Address</p>
          </div>
          <div className="cross_icon"></div>
        </div>
        <iframe allow="geolocation *" id="test-widget" ref={widgetFrameRef} src={widgetUrl}></iframe>
      </div>
    </div>
  );
};

/**
 * Complete props of Widget Popup.
 * @property {func} onPopupClose - Called when widget is closed
 * @property {func} onSubmit - Called after receiving additional details for lead
 * @property {func} onAddressSubmit - Called after receiving feasibility response
 * @property {bool} isOpen - Sets visibility of popup
 * @property {string} selectedCity - City selected by user in safo channel
 */
WidgetPopup.propTypes = {
  onPopupClose: PropTypes.func,
  onAddressSubmit: PropTypes.func,
  onSubmit: PropTypes.func,
  isOpen: PropTypes.bool,
  isLocationAllowed: PropTypes.bool,
  selectedCity: PropTypes.string,
  onCityChange: PropTypes.func,
  editButtonClick: PropTypes.number
};

export default WidgetPopup;
